import React from 'react';

/**
 * TourBubble — reusable onboarding tooltip/card
 *
 * Props:
 *   icon          — emoji or element shown in the header icon slot
 *   title         — bold heading text
 *   message       — body text
 *   stepLabel     — small progress label e.g. "Βήμα 2 από 8"
 *   actions       — array of { label, onClick, variant: 'primary'|'secondary'|'ghost' }
 *   arrowSide     — 'top'|'bottom'|null — decorative arrow direction
 *   className     — extra tailwind classes for the wrapper
 *   style         — extra inline styles for the wrapper
 */
const TourBubble = ({
  icon = null,
  title,
  message,
  stepLabel = null,
  actions = [],
  arrowSide = null,
  arrowAlign = 'left', // 'left' | 'right'
  className = '',
  style = {},
}) => {
  return (
    <div
      className={`relative rounded-2xl p-5 ${className}`}
      style={{
        background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
        border: '1px solid rgba(16,185,129,0.45)',
        boxShadow: '0 20px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(16,185,129,0.1), 0 0 30px rgba(16,185,129,0.12)',
        minWidth: '260px',
        maxWidth: '320px',
        ...style,
      }}
    >
      {/* Arrow — top */}
      {arrowSide === 'top' && (
        <div
          className={`absolute -top-[11px] ${arrowAlign === 'right' ? 'right-7' : 'left-7'}`}
          style={{
            width: 0,
            height: 0,
            borderLeft: '10px solid transparent',
            borderRight: '10px solid transparent',
            borderBottom: '11px solid rgba(16,185,129,0.45)',
          }}
        />
      )}
      {/* Arrow — bottom */}
      {arrowSide === 'bottom' && (
        <div
          className="absolute -bottom-[11px] left-7"
          style={{
            width: 0,
            height: 0,
            borderLeft: '10px solid transparent',
            borderRight: '10px solid transparent',
            borderTop: '11px solid rgba(16,185,129,0.45)',
          }}
        />
      )}

      {/* Header row */}
      <div className="flex items-start gap-3 mb-3">
        {icon && (
          <div
            className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-lg"
            style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)' }}
          >
            {icon}
          </div>
        )}
        <div className="flex-1 min-w-0">
          {stepLabel && (
            <p className="text-xs font-semibold mb-0.5" style={{ color: '#10B981' }}>
              {stepLabel}
            </p>
          )}
          <h4 className="font-semibold text-white text-sm leading-snug">{title}</h4>
        </div>
      </div>

      {/* Body */}
      <p className="text-gray-300 text-sm leading-relaxed mb-4">{message}</p>

      {/* Action buttons */}
      {actions.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {actions.map((action, i) => (
            <button
              key={i}
              onClick={action.onClick}
              className="px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-200 hover:scale-[1.03] active:scale-[0.97] whitespace-nowrap"
              style={
                action.variant === 'primary'
                  ? {
                      background: 'linear-gradient(135deg, #10B981, #059669)',
                      color: 'white',
                      boxShadow: '0 4px 14px rgba(16,185,129,0.35)',
                    }
                  : action.variant === 'ghost'
                  ? {
                      background: 'transparent',
                      color: '#6B7280',
                      border: '1px solid #374151',
                    }
                  : {
                      background: 'rgba(55,65,81,0.7)',
                      color: '#D1D5DB',
                      border: '1px solid #4A5568',
                    }
              }
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TourBubble;
